/*
 * LEPRI - LivignoItaly Early Processor for Raw image
 */

const DEFAULT_OPTIONS = {
    maxSideDimension: 1920,
    minSideDimension: 640,
    mimeType: 'image/jpeg',
    quality: 0.75
};

/**
 * Lepri given a file containing and images performs the following steps:
 * 1) resize the image by scaling the longest side to "options.maxSideDimension" and preserving the aspect ratio
 * 2) convert the image format to the one specified into "options.mimeType"
 * 3) reducing the quality of the images as specified in "options.quality"
 * 4) validate the smallest side to be at least "option.minSideDimension"
 *
 * @param {File | Blob} file containing the original image
 * @param options - { maxSideDimension: number=1920, minSideDimension: 800, mimeType: string='image/jpeg', quality: number=0.75}
 * @returns {Promise<Blob>}  Blob object containing the resized, compressed and converted(mime type) image
 */
const lepri = (file, options = {}) => {
    const opts = Object.assign(DEFAULT_OPTIONS, options);

    const checkInputPromise = new Promise((resolve, reject) => {
        // Check that file is a Blob of File that contains an image
        if (!(file instanceof Blob || file instanceof File)) {
            reject('The file given is not an instance of Blob or File');
        }
        if (!/^image/.test(file.type)) {
            reject('The file given is not an image')
        }

        resolve()
    });

    return checkInputPromise
        .then(() => getImageFromFile(file))
        .then(image => validateImage(image, opts))
        .then(image => resizeImage(image, opts))
};

/**
 * Given a File or a Blob, it return an Image
 *
 * @param {File | Blob} file
 * @returns {Promise<Image>}
 */
const getImageFromFile = (file) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        reader.onload = event => {
            const image = new Image();
            image.src = event.target.result;
            image.onload = () => {
                resolve(image);
            }
        };

        reader.onerror = error => {
            reject(error);
        };

        reader.readAsDataURL(file);
    });
};

/**
 * Given an Image raise an Exception if the minimum side dimension is smaller than "opts.minSideDimension"
 *
 * @param {Image} image
 * @param {Object} opts - { maxSideDimension: number=1920, minSideDimension: 640, mimeType: string='image/jpeg', quality: number=0.75}
 * @returns {Promise<Image>}
 */
const validateImage = (image, opts) => {
    if (image.height < opts.minSideDimension || image.width < opts.minSideDimension) {
        throw new Error("L'immagine è troppo piccola")
    }
    return new Promise((resolve, reject) => resolve(image));
}

/**
 * Given an Image if the width or height is grater than the max side dimension it resize the image
 * setting the max side to the value specified in the "opts.maxSideDimension".
 *
 * In addition this method converts the image into the mime type specified in "opts.mimeType" and set the quality to
 * "opts.quality"
 *
 * @param {Image} image
 * @param {Object} opts - { maxSideDimension: number=1920, minSideDimension: 640, mimeType: string='image/jpeg', quality: number=0.75}
 * @returns {Promise<Blob>}
 */
const resizeImage = (image, opts) => {
    const [width, height] = getScaledDimension(image.width, image.height, opts.maxSideDimension);
    const mimeType = opts.mimeType;
    const quality = opts.quality;

    // create a canvas with the desired size
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    // get the context and draw the images inside the canvas
    // width/height of the drawImage() are equal of the dimension of the canvas to cover all area
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, width, height);

    // Convert the canvas to Blob
    return new Promise((resolve, reject) => {
        const callback = (blob) => {
            resolve(blob)
        };
        canvas.toBlob(callback, mimeType, quality);
    })
};

/**
 * Given the imageWidth and the imageHeight of the image it returns a couple of width and height scaled according to the maxSide.
 * The aspect ratio will remain the same.
 *
 * @param imageWidth - original width
 * @param imageHeight - original height
 * @param maxSide - max dimension of the longest side
 * @returns {[number, number]} - couple of new width and height
 */
const getScaledDimension = (imageWidth, imageHeight, maxSide) => {
    let width, height;

    if (imageWidth >= imageHeight && imageWidth > maxSide) {
        width = maxSide;
        height = Math.round(imageHeight * (width / imageWidth));
    } else if (imageHeight > maxSide) {
        height = maxSide;
        width = Math.round(imageWidth * (height / imageHeight));
    } else {
        width = imageWidth;
        height = imageHeight;
    }

    return [width, height]
};

export default lepri;